import type { AppState } from '../../state/appState.js';
import { resolveGeometrySlotFor, resolveMaterialSlotFor } from '../../state/layerGraphSelectors.js';
import { findFirstLayerNodeIds } from '../../scene/sceneQueries.js';
import {
  type LibraryGeometryDef,
  type LibraryMaterialDef,
} from '../../scene/layerState.js';
import { cloneNodes, cloneEdges } from '../documentClone.js';
import { createUserLibraryEntityId } from '../../ids/entityIds.js';

export interface CreateLibraryItemCmd {
  type: 'library:create';
  kind: 'geometry' | 'material';
  item: LibraryGeometryDef | LibraryMaterialDef;
}

export interface SaveToLibraryCmd {
  type: 'library:save';
  kind: 'geometry' | 'material';
  item: LibraryGeometryDef | LibraryMaterialDef;
}

export interface DeleteLibraryItemCmd {
  type: 'library:delete';
  kind: 'geometry' | 'material';
  id: string;
}

export interface AttachLibraryCmd {
  type: 'library:attach';
  componentId: string | null;
  layerId?: string;
  slot: 'geometry' | 'material';
  libraryId: string;
}

export interface DetachLibraryCmd {
  type: 'library:detach';
  componentId: string | null;
  layerId?: string;
  slot: 'geometry' | 'material';
}

function createUserLibraryId(): string {
  return createUserLibraryEntityId();
}

function resolveLibrarySaveItem(
  appState: AppState,
  kind: 'geometry' | 'material',
  name: string,
  componentId: string | null,
  slot: 'geometry' | 'material',
  layerId?: string,
) {
  const scene = componentId ? appState.componentLibrary[componentId]?.scene : appState.scene;
  if (!scene) return null;

  const selectedLayerNode = layerId
    ? Object.values(scene.nodes).find(node => node.kind === 'layer' && node.layerId === layerId)
    : null;
  const resolvedLayerId = selectedLayerNode?.kind === 'layer'
    ? selectedLayerNode.layerId
    : findFirstLayerNodeIds(scene)?.layerId;
  const layerDef = resolvedLayerId ? appState.layerLibrary[resolvedLayerId] : null;
  if (!layerDef) return null;

  const sourceSlot = slot === 'geometry' ? layerDef.geometry : layerDef.material;
  const resolved = slot === 'geometry'
    ? resolveGeometrySlotFor(appState, sourceSlot)
    : resolveMaterialSlotFor(appState, sourceSlot);
  if (!resolved) return null;

  const id = createUserLibraryId();
  if (kind === 'geometry') {
    return {
      id,
      name,
      nodes: cloneNodes(resolved.nodes),
      edges: cloneEdges(resolved.edges),
      builtIn: false,
    } satisfies LibraryGeometryDef;
  }

  return {
    id,
    name,
    nodes: cloneNodes(resolved.nodes),
    edges: cloneEdges(resolved.edges),
    mode: 'color',
    builtIn: false,
  } satisfies LibraryMaterialDef;
}

export function createLibraryItemCommand(kind: 'geometry' | 'material', name: string): CreateLibraryItemCmd {
  const id = createUserLibraryId();
  const item = kind === 'geometry'
    ? {
        id,
        name,
        nodes: [],
        edges: [],
        builtIn: false,
      } satisfies LibraryGeometryDef
    : {
        id,
        name,
        nodes: [],
        edges: [],
        mode: 'color',
        builtIn: false,
      } satisfies LibraryMaterialDef;

  return { type: 'library:create', kind, item };
}

export function createSaveToLibraryCommand(
  appState: AppState,
  kind: 'geometry' | 'material',
  name: string,
  componentId: string | null,
  fromSlot: 'geometry' | 'material',
  layerId?: string,
): SaveToLibraryCmd | null {
  const item = resolveLibrarySaveItem(appState, kind, name, componentId, fromSlot, layerId);
  return item ? { type: 'library:save', kind, item } : null;
}

export function createAttachLibraryCommand(
  componentId: string | null,
  slot: 'geometry' | 'material',
  libraryId: string,
  layerId?: string,
): AttachLibraryCmd {
  return { type: 'library:attach', componentId, layerId, slot, libraryId };
}

export function createDetachLibraryCommand(
  componentId: string | null,
  slot: 'geometry' | 'material',
  layerId?: string,
): DetachLibraryCmd {
  return { type: 'library:detach', componentId, layerId, slot };
}
